import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTable, useSortBy, usePagination, useGlobalFilter } from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { faEdit, faEllipsisV, faPen, faPencil, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

const MRList = ({ permissions }) => {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const [data, setData] = useState([]);

    // Fetch conversions
    const fetchMRs = async () => {
        try {
            const response = await apiClient.get(`${SERVER_BASE}/get-mrs`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setData(response.data.data);
        } catch (error) {
            console.error("Error fetching MRs:", error);
        }
    };

    useEffect(() => {
        fetchMRs();
    }, []);
    const handleView = (row) => {
        navigate("../../MRPreview", { state: { id: row } });
      };
    // Columns configuration
    const columns = React.useMemo(
        () => [
            {
                Header: 'S.No',
                Cell: ({ row }) => row.index + 1,
            },
            {
                Header: "MR No",
                accessor: "mrNo",
            },
            {
                Header: "Date",
                accessor: "mrDate",
                Cell: ({ value }) => {
                    if (!value) return ""; // Handle cases where the date might be null or undefined
                    const date = new Date(value);
                    const day = String(date.getDate()).padStart(2, "0"); // Ensure two-digit day
                    const month = date.toLocaleString("en-US", { month: "short" }); // Abbreviated month
                    const year = String(date.getFullYear()).slice(-2); // Last two digits of the year
                    return `${day}-${month}-${year}`;
                },
            },
            {
                Header: "Project",
                accessor: "projectName",
            },
            {
                Header: "Remarks",
                accessor: "remarks",
            },
            {
                Header: "Preview",
                accessor: "Preview",
                Cell: ({ row }) => {
                    return  (
                      permissions?.read && (
                          
                        <button
                          className="btn btn-sm btn-outline-success me-1"
                          onClick={() => handleView(row.original.id)}
                        >
                        View
                        </button>
                  
                    )
                    )
                },
              },
            {
                Header: "#",
                accessor: "actions",
                Cell: ({ row }) => {
                    const {
                        createdAt,
                        createdByName,
                        updatedAt,
                        updatedByName,
                    } = row.original;
                    return (
                        <div className="dropdown">
                            <button
                                className="btn btn-light btn-sm"
                                type="button"
                                id={`dropdownMenuButton-${row.index}`}
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <FontAwesomeIcon icon={faEllipsisV} />
                            </button>

                            <ul
                                className="dropdown-menu dropdown-menu-end"
                                aria-labelledby={`dropdownMenuButton-${row.index}`}
                            >
                                {/* Edit */}
                                {permissions?.edit && (
                                    <li>
                                        <button
                                            className="dropdown-item"
                                            onClick={() =>
                                                navigate("../MRInfo", { state: { editData: row.original } })
                                            }
                                        >
                                            <FontAwesomeIcon icon={faEdit} /> Edit
                                        </button>
                                    </li>
                                )}
                                <li><hr className="dropdown-divider" /></li>

                                {/* Created By / Updated By */}
                                {createdByName && (
                                    <li className="dropdown-item">
                                        <FontAwesomeIcon icon={faPlus} /> {createdByName}
                                        {createdAt &&
                                            ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                                    </li>
                                )}

                                {/* Updated Info */}
                                {updatedByName && (
                                    <li className="dropdown-item">
                                        <FontAwesomeIcon icon={faPencil} /> {updatedByName}
                                        {updatedAt &&
                                            ` (${moment(updatedAt).format("DD-MMM-YY hh:mm A")})`}
                                    </li>
                                )}
                            </ul>
                        </div>
                    );
                }
            }

        ],
        [navigate, permissions]
    );

    // Table instance with search, sort, and pagination
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        state,
        setGlobalFilter,
        canPreviousPage,
        canNextPage,
        pageOptions,
        nextPage,
        previousPage,
        setPageSize,
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 20 },
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const { globalFilter, pageIndex, pageSize } = state;

    return (
        <div className="formcontainer mt-5">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Material Requisition</h4>

            </div>

            {/* Search */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                {/* Add MR Button */}
                {permissions?.write && (
                    <button
                        className="btn btn-outline-success"
                        onClick={() => navigate("../MRInfo")}
                    >
                        Add MR
                    </button>
                )}

                {/* Search input + Page Size */}
                {permissions?.read && (<>
                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                        {/* Search Input */}
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search"
                            value={globalFilter || ""}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                        />

                        {/* Page Size Dropdown */}
                        <label className="d-inline-flex align-items-center mb-0 gap-2">
                            <select
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                className="form-select w-auto"
                            >
                                {[20, 30, 50, 100].map((size) => (
                                    <option key={size} value={size}>
                                        Show {size}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </>)}
            </div>


            {/* Table */}
            <table
                className="table table-striped table-bordered"
                {...getTableProps()}
                style={{ fontSize: '13px' }}
            >
                <thead className="table-light">
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    style={{ cursor: "pointer", }}
                                >
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted
                                            ? column.isSortedDesc
                                                ? " 🔽"
                                                : " 🔼"
                                            : ""}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                    {page.length > 0 ? (
                        page.map((row) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => (
                                        <td
                                            {...cell.getCellProps()}
                                            style={{ verticalAlign: "middle" }}
                                        >
                                            {cell.render("Cell")}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={columns.length} className="text-center">
                                No conversions found
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>


            {/* Pagination Controls */}
            <div className="d-flex align-items-center mb-3">
                {/* Previous – left corner */}
                <button
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    className="btn btn-secondary me-auto"
                    style={{ minWidth: 110 }}   // keeps width consistent
                >
                    Previous
                </button>

                {/* Center block for page index & size selector */}
                <div className="text-center flex-grow-1 d-flex flex-column align-items-center">
                    {/* Row 1: Page index and size selector in one line */}
                    <div className="d-flex align-items-center gap-3">
                        <div className="fw-semibold">
                            Page {pageIndex + 1} of {pageOptions.length}
                        </div>


                    </div>
                </div>


                {/* Next – right corner */}
                <button
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                    className="btn btn-secondary ms-auto"
                    style={{ minWidth: 110 }}
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default MRList;
